/**
 * Client-side base path detection for path-based multi-tenancy.
 *
 * Under path-based multi-tenancy the experience app is served under a tenant prefix
 * (e.g. `/t-default/sign-in`). The server mounts the tenant app at `/<tenantId>` but
 * the bundled client has no build-time knowledge of that prefix, so absolute paths
 * like `/api/...` would skip the tenant mount and hit the root dispatcher — which
 * interprets the first segment ("api") as a tenant id and 404s.
 *
 * We derive the base path from the initial `window.location.pathname` by splitting
 * on the first segment that matches a known experience route. Computed once at
 * module load to avoid being invalidated by client-side navigations.
 */

const experienceRouteSegments = new Set([
  'sign-in',
  'register',
  'forgot-password',
  'reset-password',
  'consent',
  'callback',
  'direct',
  'springboard',
  'unknown-session',
  'create-passkey',
  'mfa-binding',
  'mfa-verification',
  'mfa-onboarding',
  'switch-account',
  'one-time-token',
  'continue',
  'social',
  'single-sign-on',
  'device',
  'identifier-sign-in',
  'identifier-register',
]);

const computeBasePath = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const segments = window.location.pathname.split('/').filter(Boolean);
  const routeIndex = segments.findIndex((segment) => experienceRouteSegments.has(segment));

  if (routeIndex <= 0) {
    return '';
  }

  return '/' + segments.slice(0, routeIndex).join('/');
};

export const basePath = computeBasePath();

/**
 * Rewrite an absolute-path request URL to include the tenant base path. Returns a
 * new Request when a rewrite is needed, otherwise `undefined` so ky keeps the
 * original request. Absolute URLs on a different origin and already-prefixed paths
 * are left alone.
 */
export const prefixRequestWithBasePath = (request: Request): Request | undefined => {
  if (!basePath) {
    return;
  }

  const url = new URL(request.url);

  if (url.origin !== window.location.origin) {
    return;
  }

  if (url.pathname === basePath || url.pathname.startsWith(basePath + '/')) {
    return;
  }

  const prefixedUrl = new URL(basePath + url.pathname + url.search + url.hash, url.origin);
  return new Request(prefixedUrl.toString(), request);
};
